import React, { useEffect, useState } from "react";
import {
    Modal, ModalHeader, ModalBody, ModalFooter,
    Button, Form, FormGroup, Label, Input, Alert, Row, Col
} from "reactstrap";
import { toast } from "react-hot-toast";
import { renewExpiredMembership } from "../service/paymentService";
import { fetchAllMemberships } from "../../Clients/service/membershipService";
import { useSede } from "context/SedeContext";

const RenewMembershipModal = ({ isOpen, toggle, payment, onRenewed }) => {
    const { addSedeParams, addSedeHeaders } = useSede();
    const [loading, setLoading] = useState(false);
    const [memberships, setMemberships] = useState([]);
    const [form, setForm] = useState({
        membership_id: '',
        valid_from: '',
        valid_until: '',
        payment_method: 'Manual'
    });

    useEffect(() => {
        if (isOpen && payment) {
            // Cargar membresías disponibles
            loadMemberships();
            
            // Establecer valores por defecto
            const today = new Date().toISOString().split('T')[0];
            const thirtyDaysLater = new Date();
            thirtyDaysLater.setDate(thirtyDaysLater.getDate() + 30);
            const validUntil = thirtyDaysLater.toISOString().split('T')[0];
            
            setForm({
                membership_id: payment.membership?.id || '',
                valid_from: today,
                valid_until: validUntil,
                payment_method: 'Manual'
            });
        }
    }, [isOpen, payment]);

    const loadMemberships = async () => {
        try {
            const sedeParams = addSedeParams({});
            const sedeHeaders = addSedeHeaders({});
            const data = await fetchAllMemberships(sedeParams, sedeHeaders);
            setMemberships(data || []);
        } catch (error) {
            console.error('Error al cargar membresías:', error);
            toast.error('Error al cargar membresías disponibles');
        }
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        setForm((prev) => ({ ...prev, [name]: value }));
        
        // Si cambia la membresía, actualizar el precio sugerido
        if (name === 'membership_id') {
            const selectedMembership = memberships.find(m => m.id === parseInt(value));
            if (selectedMembership) {
                // No actualizamos el monto automáticamente, pero podríamos mostrar una sugerencia
            }
        }
        
        // Si cambia valid_from, calcular automáticamente valid_until si está vacío
        if (name === 'valid_from' && !form.valid_until) {
            const fromDate = new Date(value);
            const untilDate = new Date(fromDate);
            untilDate.setDate(untilDate.getDate() + 30);
            setForm((prev) => ({ ...prev, valid_until: untilDate.toISOString().split('T')[0] }));
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        
        if (!form.membership_id) {
            toast.error('Debes seleccionar una membresía');
            return;
        }
        
        if (!form.valid_from) {
            toast.error('Debes seleccionar una fecha de inicio');
            return;
        }
        
        if (!form.valid_until) {
            toast.error('Debes seleccionar una fecha de fin');
            return;
        }
        
        if (new Date(form.valid_until) <= new Date(form.valid_from)) {
            toast.error('La fecha de fin debe ser posterior a la fecha de inicio');
            return;
        }

        try {
            setLoading(true);
            
            // Validar que tenemos el ID del pago
            if (!payment || !payment.id) {
                toast.error('Error: No se pudo identificar el pago a renovar');
                return;
            }
            
            console.log('Renovando membresía para pago:', payment.id, 'Cliente:', payment.client || payment.client_name);
            
            const sedeParams = addSedeParams({});
            const sedeHeaders = addSedeHeaders({});
            
            // Llamar al endpoint de renovación con los parámetros personalizados
            const customData = {
                membership_id: parseInt(form.membership_id),
                valid_from: form.valid_from,
                valid_until: form.valid_until,
            };
            
            console.log('Datos a enviar:', { payment_id: payment.id, customData });
            
            await renewExpiredMembership(
                payment.id,
                form.payment_method,
                sedeParams,
                sedeHeaders,
                customData
            );
            
            toast.success('Membresía renovada exitosamente');
            onRenewed?.();
            toggle();
        } catch (err) {
            console.error('Error al renovar membresía:', err);
            console.error('Response:', err.response?.data);
            const errorMessage = err.response?.data?.error || err.response?.data?.detail || err.message || 'Error al renovar membresía';
            toast.error(errorMessage);
        } finally {
            setLoading(false);
        }
    };

    const selectedMembership = memberships.find(m => m.id === parseInt(form.membership_id));

    return (
        <Modal isOpen={isOpen} toggle={toggle} size="lg">
            <ModalHeader toggle={toggle}>
                <i className="fa fa-sync-alt mr-2"></i>
                Renovar Membresía
            </ModalHeader>
            <Form onSubmit={handleSubmit}>
                <ModalBody>
                    {payment && (
                        <Alert color="warning" className="mb-3">
                            <strong>Cliente:</strong> {payment.client || payment.client_name || `${payment.client_first_name || ''} ${payment.client_last_name || ''}`.trim() || 'N/A'}<br />
                            <strong>ID del Pago:</strong> {payment.id || 'N/A'}<br />
                            <strong>Membresía Actual:</strong> {payment.membership?.name || payment.membership_name || 'N/A'}<br />
                            <strong>Válido Hasta:</strong> {payment.valid_until ? String(payment.valid_until).split(' ')[0] : 'N/A'}
                        </Alert>
                    )}
                    
                    <FormGroup>
                        <Label for="membership_id">
                            <strong>Seleccionar Membresía *</strong>
                        </Label>
                        <Input
                            type="select"
                            name="membership_id"
                            id="membership_id"
                            value={form.membership_id}
                            onChange={handleChange}
                            required
                        >
                            <option value="">Selecciona una membresía</option>
                            {memberships.map((membership) => (
                                <option key={membership.id} value={membership.id}>
                                    {membership.name} - Q{parseFloat(membership.price || 0).toFixed(2)}
                                </option>
                            ))}
                        </Input>
                        {selectedMembership && (
                            <small className="text-muted mt-1 d-block">
                                Precio: Q{parseFloat(selectedMembership.price || 0).toFixed(2)}
                            </small>
                        )}
                    </FormGroup>

                    <Row>
                        <Col md="6">
                            <FormGroup>
                                <Label for="valid_from">
                                    <strong>Válido Desde *</strong>
                                </Label>
                                <Input
                                    type="date"
                                    name="valid_from"
                                    id="valid_from"
                                    value={form.valid_from}
                                    onChange={handleChange}
                                    required
                                />
                                <small className="text-muted mt-1 d-block">
                                    Fecha de inicio de la membresía
                                </small>
                            </FormGroup>
                        </Col>
                        <Col md="6">
                            <FormGroup>
                                <Label for="valid_until">
                                    <strong>Válido Hasta *</strong>
                                </Label>
                                <Input
                                    type="date"
                                    name="valid_until"
                                    id="valid_until"
                                    value={form.valid_until}
                                    onChange={handleChange}
                                    required
                                    min={form.valid_from}
                                />
                                <small className="text-muted mt-1 d-block">
                                    Fecha de fin de la membresía
                                </small>
                            </FormGroup>
                        </Col>
                    </Row>

                    <FormGroup>
                        <Label for="payment_method">
                            <strong>Método de Pago</strong>
                        </Label>
                        <Input
                            type="select"
                            name="payment_method"
                            id="payment_method"
                            value={form.payment_method}
                            onChange={handleChange}
                        >
                            <option value="Manual">Manual</option>
                            <option value="Efectivo">Efectivo</option>
                            <option value="Transferencia">Transferencia</option>
                            <option value="VisaLink">VisaLink</option>
                        </Input>
                    </FormGroup>

                    {form.valid_from && form.valid_until && (
                        <Alert color="info" className="mt-3">
                            <strong>Resumen:</strong><br />
                            La membresía será válida desde <strong>{form.valid_from}</strong> hasta <strong>{form.valid_until}</strong>
                            {selectedMembership && (
                                <>
                                    <br />
                                    Con un costo de <strong>Q{parseFloat(selectedMembership.price || 0).toFixed(2)}</strong>
                                </>
                            )}
                        </Alert>
                    )}
                </ModalBody>
                <ModalFooter>
                    <Button color="secondary" onClick={toggle} disabled={loading}>
                        Cancelar
                    </Button>
                    <Button color="success" type="submit" disabled={loading}>
                        {loading ? (
                            <>
                                <span className="spinner-border spinner-border-sm mr-2" role="status" aria-hidden="true"></span>
                                Renovando...
                            </>
                        ) : (
                            <>
                                <i className="fa fa-sync-alt mr-2"></i>
                                Renovar Membresía
                            </>
                        )}
                    </Button>
                </ModalFooter>
            </Form>
        </Modal>
    );
};

export default RenewMembershipModal;

